/**
 * @module lib/api
 * Axios client and time-formatting helpers for the customer frontend.
 */

import axios from "axios";
import logger from "./logger";

/**
 * @typedef {import('./types').BookingSlot} BookingSlot
 * @typedef {import('./types').LocalTimeParts} LocalTimeParts
 * @typedef {import('./types').ApiResponse} ApiResponse
 */

const APP_ENV = (process.env.REACT_APP_ENV || process.env.ENV || "PROD").toUpperCase();

const BACKEND_URL =
  APP_ENV === "LOCAL"
    ? (process.env.REACT_APP_BACKEND_URL_LOCAL || "http://localhost:8000")
    : (process.env.REACT_APP_BACKEND_URL || "https://api.avyug.com");

/** Base URL for all `/api` routes (includes trailing `/api`). */
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

/**
 * Axios instance for backend API calls.
 * Adds auth token if present and logs requests/responses for debugging.
 */
export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("Avyug_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // normalize trailing slash on request URL (avoid FastAPI redirects)
  if (config.url && config.url !== "/" && config.url.endsWith("/")) {
    config.url = config.url.replace(/\/+$/, "");
  }
  logger.info("api.request", { method: config.method, url: config.url });
  return config;
});

api.interceptors.response.use(
  (resp) => {
    logger.info("api.response", { status: resp.status, url: resp.config.url });
    return resp;
  },
  (err) => {
    logger.error("api.error", err);
    return Promise.reject(err);
  }
);

/**
 * Format a Date (or timestamp) into local time with AM/PM and timezone abbreviation.
 * Uses `Intl.DateTimeFormat` with `hour12: true` and `timeZoneName: 'short'` so
 * the output includes AM/PM and a short timezone like "IST", "PDT", or "PST".
 * Intl applies DST rules for the chosen time zone when `timeZone` is provided.
 *
 * @param {Date|string|number} [date=new Date()] - A Date object or any value accepted by `new Date()`.
 * @param {string} [timeZone] - Optional IANA time zone (e.g. `'America/Los_Angeles'`). Omit for the environment default.
 * @returns {string} Formatted time string including AM/PM and short timezone (e.g. `"09:30:45 AM PDT"`).
 */
export function formatLocalTime(date = new Date(), timeZone) {
  const d = date instanceof Date ? date : new Date(date);
  const options = {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short",
  };
  try {
    const dtf = timeZone
      ? new Intl.DateTimeFormat(undefined, { ...options, timeZone })
      : new Intl.DateTimeFormat(undefined, options);
    return dtf.format(d);
  } catch (e) {
    // Fallback to toLocaleTimeString if Intl constructor with timeZone fails
    try {
      return d.toLocaleTimeString(undefined, { ...options, timeZone });
    } catch (e2) {
      // Last-resort manual formatting (no timezone abbrev)
      const hh = d.getHours();
      const ampm = hh >= 12 ? "PM" : "AM";
      const hour12 = hh % 12 || 12;
      const mm = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      return `${hour12}:${mm}:${ss} ${ampm}`;
    }
  }
}

/**
 * Return the runtime's IANA time zone string for the local environment
 * (e.g. `'Asia/Kolkata'` or `'America/Los_Angeles'`). Pair with {@link formatLocalTime}
 * when formatting for a specific zone.
 *
 * @returns {string|null} IANA timezone name, or `null` when unavailable.
 */
export function getLocalTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (e) {
    return null;
  }
}
